'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Drawer as VaulDrawer } from 'vaul';
import { cva, type VariantProps } from 'class-variance-authority';
import { ArrowLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '@restworld/utils-common';

import { IconButton } from '../icon-button';

import { useMediaQuery } from './use-media-query';

// ---------------------------------------------------------------------------
// Internal context to pass onClose + isDesktop through the tree
// ---------------------------------------------------------------------------

const ModalInternalContext = React.createContext<{
  isDesktop: boolean;
  onClose: () => void;
}>({ isDesktop: true, onClose: () => undefined });

// ---------------------------------------------------------------------------
// Modal Root — auto-switches between Dialog (desktop) and Drawer (mobile)
// ---------------------------------------------------------------------------

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  /** Disable closing when clicking the backdrop overlay */
  disableOverlayClose?: boolean;
}

export function Modal({ open, onOpenChange, children, disableOverlayClose }: ModalProps) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const onClose = React.useCallback(() => onOpenChange(false), [onOpenChange]);

  const contextValue = React.useMemo(() => ({ isDesktop, onClose }), [isDesktop, onClose]);

  if (isDesktop) {
    return (
      <ModalInternalContext.Provider value={contextValue}>
        <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
          <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay
              className="fixed inset-0 z-[100] bg-black/30 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:duration-200 data-[state=open]:ease-out data-[state=closed]:duration-300 data-[state=closed]:ease-in"
              onClick={disableOverlayClose ? (e) => e.stopPropagation() : undefined}
            />
            {children}
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
      </ModalInternalContext.Provider>
    );
  }

  return (
    <ModalInternalContext.Provider value={contextValue}>
      <VaulDrawer.Root open={open} onOpenChange={onOpenChange} dismissible={!disableOverlayClose}>
        <VaulDrawer.Portal>
          <VaulDrawer.Overlay className="fixed inset-0 z-[100] bg-black/30" />
          {children}
        </VaulDrawer.Portal>
      </VaulDrawer.Root>
    </ModalInternalContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// ModalContent — the panel itself
// ---------------------------------------------------------------------------

const modalContentVariants = cva('', {
  variants: {
    variant: {
      center: 'fixed left-1/2 top-1/2 z-[101] -translate-x-1/2 -translate-y-1/2 rounded-2xl',
      right: 'fixed right-0 top-0 z-[101] h-full w-[391px] rounded-l-2xl',
    },
    size: {
      sm: 'w-full max-w-sm',
      md: 'w-full max-w-md',
      lg: 'w-full max-w-lg',
      xl: 'w-full max-w-xl',
      '2xl': 'w-full max-w-2xl',
      '3xl': 'w-full max-w-3xl',
      '4xl': 'w-full max-w-4xl',
      full: 'w-full max-w-[640px]',
    },
  },
  defaultVariants: {
    variant: 'center',
    size: 'md',
  },
});

export interface ModalContentProps extends VariantProps<typeof modalContentVariants> {
  children: React.ReactNode;
  className?: string;
  /** Title shown in the header. Pass null/undefined to hide header. */
  title?: string | React.ReactNode;
  /** Show the close X button. Default: true */
  showCloseIcon?: boolean;
  /** Footer content */
  footer?: React.ReactNode;
  /** Remove default body padding */
  noPadding?: boolean;
  /** Show a back button in the header */
  showBackButton?: boolean;
  /** Handler for back button */
  onBack?: () => void;
  /** Hide the header completely */
  hideHeader?: boolean;
  /** Extra classes for the header row (e.g. a scroll-dependent bottom border). */
  headerClassName?: string;
  /**
   * Mobile drawer only. When false, swiping inside the body scrolls it and never drags the drawer
   * closed: only the handle and header still swipe-to-dismiss. Use it for scrollable bodies with
   * inputs or nested scroll areas (e.g. address autocomplete), where Vaul reads a downward scroll
   * that reaches the top as a dismiss gesture. Default: true.
   */
  dismissOnBodyDrag?: boolean;
}

export const ModalContent = React.forwardRef<HTMLDivElement, ModalContentProps>(
  (
    {
      children,
      className,
      variant = 'center',
      size = 'md',
      title,
      showCloseIcon = true,
      footer,
      noPadding,
      showBackButton,
      onBack,
      hideHeader,
      headerClassName,
      dismissOnBodyDrag = true,
    },
    ref
  ) => {
    const { isDesktop, onClose } = React.useContext(ModalInternalContext);

    const header = !hideHeader && (
      <ModalHeader
        isDesktop={isDesktop}
        onClose={onClose}
        showCloseIcon={showCloseIcon}
        showBackButton={showBackButton}
        onBack={onBack}
        className={headerClassName}
      >
        {title}
      </ModalHeader>
    );

    const body = (
      <div
        className={cn('flex-1 overflow-y-auto', noPadding ? '' : 'px-6 py-4')}
        // Vaul skips drag handling for targets inside [data-vaul-no-drag]; the Dialog ignores it.
        data-vaul-no-drag={dismissOnBodyDrag ? undefined : ''}
      >
        {children}
      </div>
    );

    const footerEl = footer && (
      <div className={cn('border-t border-gray-100', noPadding ? '' : 'px-6 py-4')}>{footer}</div>
    );

    if (isDesktop) {
      return (
        <DialogPrimitive.Content
          ref={ref}
          // Prevent Dialog from closing when clicking on portal-based dropdowns
          // (e.g. Combobox/Select portals rendered at document.body level)
          onPointerDownOutside={(e) => {
            const target = e.target as HTMLElement;
            if (target?.closest?.('[role="listbox"], [role="option"]')) {
              e.preventDefault();
            }
          }}
          onInteractOutside={(e) => {
            const target = e.target as HTMLElement;
            if (target?.closest?.('[role="listbox"], [role="option"]')) {
              e.preventDefault();
            }
          }}
          className={cn(
            modalContentVariants({ variant, size: variant === 'right' ? undefined : size }),
            'bg-white shadow-lg flex flex-col',
            // The right-side drawer is a full-height panel (fixed top-0 h-full); only cap the height for
            // centered modals, which must not exceed the viewport. max-h-[85vh] on `right` left a gap.
            variant !== 'right' && 'max-h-[85vh]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            // Match master's modal timing (enter ~200ms ease-out, leave ~300ms ease-in) so the close
            // reads as a smooth fade rather than an abrupt cut. tailwindcss-animate maps duration-*/
            // ease-* onto animation-duration / animation-timing-function (the default was a quick 150ms).
            'data-[state=open]:duration-200 data-[state=open]:ease-out data-[state=closed]:duration-300 data-[state=closed]:ease-in',
            variant === 'center' &&
              // Zoom + fade in place at true centre. tailwindcss-animate's enter/exit keyframe
              // animates the whole `transform`, so it wipes the -translate-x/y-1/2 centering while
              // the animation runs — feed the same -50% offset back via slide-*-1/2, otherwise the
              // panel appears to fly in from the bottom-right corner. Matches the shadcn dialog pattern.
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-1/2 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-1/2',
            variant === 'right' && 'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
            className
          )}
          // No Description rendered by default: opt out of Radix's description requirement so it
          // doesn't warn. Callers needing one can pass a <ModalDescription> child.
          aria-describedby={undefined}
        >
          {/* Radix requires a DialogTitle inside DialogContent for screen readers. Render the
              provided title (or a neutral fallback) visually-hidden so every modal is accessible
              even when it draws its own heading inside the body. */}
          <DialogPrimitive.Title className="sr-only">
            {typeof title === 'string' && title ? title : 'Dialog'}
          </DialogPrimitive.Title>
          {header}
          {body}
          {footerEl}
        </DialogPrimitive.Content>
      );
    }

    // Mobile: Vaul Drawer
    return (
      <VaulDrawer.Content
        ref={ref}
        className={cn(
          'fixed bottom-0 left-0 right-0 z-[101] bg-white rounded-t-2xl flex flex-col max-h-[90dvh]',
          className
        )}
      >
        {/* Drag handle */}
        <div className="flex w-full justify-center pt-3 pb-2 touch-none">
          <div className="h-1 w-10 rounded-full bg-gray-300" />
        </div>
        {header}
        {body}
        <div className={cn(noPadding ? '' : 'pb-safe')}>{footerEl}</div>
      </VaulDrawer.Content>
    );
  }
);
ModalContent.displayName = 'ModalContent';

// ---------------------------------------------------------------------------
// ModalHeader — internal header with close/back buttons
// ---------------------------------------------------------------------------

interface ModalHeaderProps {
  children?: React.ReactNode;
  className?: string;
  showCloseIcon?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
  isDesktop: boolean;
  onClose: () => void;
}

function ModalHeader({
  children,
  className,
  showCloseIcon = true,
  showBackButton,
  onBack,
  isDesktop,
  onClose,
}: ModalHeaderProps) {
  const closeButton = showCloseIcon && <IconButton Icon={XMarkIcon} ariaLabel="Close" size="md" onClick={onClose} />;
  // Back mirrors the close button: same IconButton and size, same vertical placement, left edge.
  const backButton = showBackButton && <IconButton Icon={ArrowLeftIcon} ariaLabel="Back" size="md" onClick={onBack} />;

  return (
    // The close button is positioned absolutely (right edge, vertically centered) instead of taking a
    // slot in the header flex row. This lets a centered title (align="center") center across the FULL
    // header width rather than only the space left of the button — which previously made centered
    // titles read as shifted left. String titles stay left-aligned and truncate (pr-10 reserves room
    // for the button); the back button mirrors it on the left edge.
    <div className={cn('relative flex items-center gap-2 px-6 py-4', className)}>
      {backButton && (
        <div className={cn('absolute left-4', children ? 'top-1/2 -translate-y-1/2' : 'top-4')}>{backButton}</div>
      )}
      {children && (
        <div className={cn('flex-1 min-w-0', backButton && 'pl-10')}>
          {typeof children === 'string' ? (
            // Centered to match master's BaseModal (CenteredModalHeader): text-xl, centered, wraps if long.
            // The close button is absolute (out of flow), so the title centers across the full header width.
            <h3 className="text-xl font-semibold text-gray-900 leading-6 text-center">{children}</h3>
          ) : (
            children
          )}
        </div>
      )}
      {closeButton && (
        // With a title, center the button vertically so it aligns with the title. Title-less headers
        // are a thin py-4 strip, so centering leaves the button floating above the body — tuck it into
        // the top-right corner instead (matches master's corner close X).
        <div className={cn('absolute right-4', children ? 'top-1/2 -translate-y-1/2' : 'top-4')}>{closeButton}</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ModalTitle / ModalDescription — accessibility helpers
// ---------------------------------------------------------------------------

export const ModalTitle = DialogPrimitive.Title;
export const ModalDescription = DialogPrimitive.Description;

export { modalContentVariants };
